/**
 * Chat turns the person's plan refuses (sandbox-first experience, docs/SANDBOX_FIRST.md).
 *
 * mia-flow-bff answers a chat turn (`POST …/run` or `…/run_sse` through `/api/adk-proxy`) with
 * 403 `{ error: "<sentence>", code: "plan_feature" }` when the plan leaves the organization's models out and the
 * person has no own key in use (mia-flow-bff lib/plan-features.js). Those turns go through the chat widget's own
 * transport, which gives Flow no hook for the response, so index.jsx wraps the global fetch with
 * `watchPlanRefusals`: the response is handed back untouched, and a refusal is announced to whoever listens
 * (`onPlanRefusal`, the chat panel's notice).
 */
export const PLAN_FEATURE = 'plan_feature';
const REFUSAL_EVENT = 'flow:plan-refusal';
// The same test mia-flow-bff (routes/adk-proxy.js) and adk-be-app use for a chat turn.
const CHAT_TURN = /\/run(?:_sse)?$/;

function requestUrl(input) {
  if (typeof input === 'string') { return input; }
  return input?.url || (input instanceof URL ? input.href : '');
}

function requestMethod(input, init) {
  return String(init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
}

/** Whether a request is a chat turn: a POST whose path ends in `/run` or `/run_sse`. */
export function isChatTurn(input, init) {
  if (requestMethod(input, init) !== 'POST') { return false; }
  const path = requestUrl(input).split(/[?#]/)[0];
  return CHAT_TURN.test(path);
}

/** `{ message }` when a response is the plan's refusal, else null. Reads a clone, never the response itself. */
export async function planRefusalOf(response) {
  if (!response || response.status !== 403) { return null; }
  try {
    const body = await response.clone().json();
    if (body?.code !== PLAN_FEATURE) { return null; }
    return { message: typeof body.error === 'string' ? body.error : '' };
  } catch {
    return null;
  }
}

/** Tells the listeners a chat turn was refused by the plan. */
export function reportPlanRefusal(refusal) {
  window.dispatchEvent(new CustomEvent(REFUSAL_EVENT, { detail: refusal }));
}

/** Calls `listener({ message })` on every refused chat turn; returns the unsubscribe. */
export function onPlanRefusal(listener) {
  const handle = (event) => listener(event.detail || { message: '' });
  window.addEventListener(REFUSAL_EVENT, handle);
  return () => window.removeEventListener(REFUSAL_EVENT, handle);
}

/** `fetchImpl`, plus: a chat turn answered with the plan's refusal is reported. Every response is returned as is. */
export function watchPlanRefusals(fetchImpl) {
  return async(input, init) => {
    const response = await fetchImpl(input, init);
    if (response?.status === 403 && isChatTurn(input, init)) {
      const refusal = await planRefusalOf(response);
      if (refusal) { reportPlanRefusal(refusal); }
    }
    return response;
  };
}
